import type {
  HubAlgorithmDefinition,
  HubMetricArtifact,
} from '@robovision/hub'
import {
  type MetricsDatapointField,
  type MetricsDatapointFieldType,
  MetricsMetricWidget,
} from '@robovision/metrics'
import {
  JobBaseArtifact,
  type JobBaseArtifactParams,
} from './JobBaseArtifact'


export interface JobMetricArtifactParams extends JobBaseArtifactParams {
  fields: MetricsDatapointField[]
  name: string
  widget?: MetricsMetricWidget
}

export class JobMetricArtifact extends JobBaseArtifact {
  public fields: MetricsDatapointField[]
  public override name: string = 'JobMetricArtifact default name'
  public widget?: MetricsMetricWidget

  protected constructor (params: JobMetricArtifactParams) {
    super(params)
    this.fields = params.fields
    this.widget = params.widget
    this.name = params.name
  }

  public get isListMetric () {
    return (this.widget === MetricsMetricWidget.Scalar || this.widget === MetricsMetricWidget.Percentage) &&
      this.fields?.length > 1
  }

  // TODO: https://robovision.atlassian.net/browse/PD-9177
  public get isSupportedListMetric () {
    return this.isListMetric && this.fields?.length === 2
  }

  public static fromDefinition (
    metricArtifact: HubMetricArtifact,
    algorithmDefinition: HubAlgorithmDefinition,
  ) {
    const def = algorithmDefinition.metricDefinitions?.find(
      (def) => def.id === metricArtifact.metricDefinition,
    )

    if (!def) {
      throw new Error(
        `Invalid schema. Metric definition ${metricArtifact.metricDefinition} not found`,
      )
    }

    // we do some mapping here because we use the types from @robovision/metrics everywhere
    // but we load the types from the hub. the enum conversions are type unsafe, so we assume
    // the types are always in sync
    return new JobMetricArtifact({
      artifactId: metricArtifact.id,
      definitionId: def.id,
      description: metricArtifact.description ?? def.description,
      fields: def.fields.map(f => ({
        description: f.description,
        id: f.id,
        name: f.name,
        sortable: f.sortable,
        type: f.type as unknown as MetricsDatapointFieldType,
        uniqueConstraint: f.uniqueConstraint,
      })),
      name: metricArtifact.name ?? def.name,
      widget: def.widget ? def.widget as unknown as MetricsMetricWidget : undefined,
    })
  }
}
